import { getInvites } from 'api/invites.api';
import { getRoles } from 'api/roles.api';
import { getOrgTeams } from 'api/teams.api';
import { getOrgUsers } from 'api/users.api';
import { $list, $user, $view } from 'signals/global.signals';
import displayError from 'utils/displayError';
import { createUserOptions, filterActiveUsers } from 'utils/userFilters';

export const fetchAndSetUsersTeamsAndRoles = async () => {
  try {
    const [{ data: userData }, { data: teams }, { data: roles }] = await Promise.all([
      getOrgUsers($user.value.organization.id),
      getOrgTeams($user.value.organization.id),
      getRoles({ userType: $user.value.userType }),
    ]);

    // Filter out archived users for options
    const userOptions = createUserOptions(userData);

    const teamOptions = teams.map((team) => ({
      label: team.name,
      value: team.id,
    }));

    const roleOptions = roles.map((role) => ({
      label: role.description,
      value: role.id,
    }));

    $view.update({
      userOptions,
      teamOptions,
      roleOptions,
      organizationName: $user.value.organization?.name || '',
      logo: $user.value.organization?.logoImage || '',
      logoColors: $user.value.organization?.logoColors || [],
    });
  } catch (error) {
    displayError({
      message: 'Failed to load team options. Please refresh the page.',
      title: 'Loading Error',
    });
  }
};

export const fetchAndSetInvestorTeamData = async () => {
  $view.update({
    isTableLoading: true,
  });

  try {
    const { data } = await getOrgTeams($user.value.organization.id);

    $list.update({ data });
  } catch (error) {
    displayError({
      message: 'Failed to load team data. Please refresh the page.',
      title: 'Loading Error',
    });
  } finally {
    $view.update({
      isTableLoading: false,
    });
  }
};

export const fetchAndSetOrganizationUsers = async () => {
  $view.update({
    isLoading: true,
    error: null,
    isTableLoading: true,
  });
  $list.reset();

  try {
    const [{ data: users }, { data: teams }] = await Promise.all([
      getOrgUsers($user.value.organization.id),
      getOrgTeams($user.value.organization.id),
    ]);

    // Filter out archived users before processing
    const activeUsers = filterActiveUsers(users);

    activeUsers.forEach((user, userIdx) => {
      activeUsers[userIdx].teams = teams
        .filter((team) => team.users.find((teamUser) => teamUser.id === user.id))
        .map((team) => team.id);
    });

    $list.update({ data: activeUsers });
  } catch (error) {
    displayError({
      message: 'Failed to load organization users. Please refresh the page.',
      title: 'Loading Error',
    });
  } finally {
    $view.update({
      isLoading: false,
      isTableLoading: false,
    });
  }
};

export const fetchAndSetInvestorInvites = async () => {
  $view.update({
    isTableLoading: true,
  });

  try {
    const { data } = await getInvites();
    $list.update({ data });
  } catch (error) {
    displayError({
      message: 'Failed to load invitations. Please refresh the page.',
      title: 'Loading Error',
    });
  } finally {
    $view.update({
      isTableLoading: false,
    });
  }
};
